import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Folio, Engraving, Glossarium, Marginalia, StaffExample } from "@/components/sn/SNShell";
import { Notation } from "@/components/Notation";

export default function StaffNotation2() {
  const { lesson, course, accent } = findLesson("staff-notation", "2");
  return (
    <LessonShell
      course={course}
      courseSlug="staff-notation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/staff-notation/SN2-Treble-Clef-Animated.mp4"
        caption="The treble clef — 5 lines (EGBDF) + 4 spaces (FACE)"
        hint="≈ 24 sec · watch first"
      />
      <Folio number="I" title="The G clef — and why it's called that">
        <p>
          The treble clef has another name: the <strong>G clef</strong>. The reason is the dot
          at the centre of the symbol. When you draw it carefully, the dot lands on the second
          line of the staff — and that position is G. The dot point is G. That's why it's the
          G clef.
        </p>
        <p>
          Drawing tip: Jason says put a dot, then make a curve, then a little more, and finish
          it off. If you can't manage the freehand drawing, use a manuscript book (available
          at any local store — the lines come pre-drawn) or an app like MuseScore. The clef
          will be there for you.
        </p>
        <p>
          Once the clef is in place, you can start notating things. But first you need to know
          where the notes are. And for that, there is one master key Jason wants you to remember:
          the interval of a <strong>third</strong>.
        </p>
      </Folio>

      <Folio number="II" title="The lines — and the interval of a third">
        <p>
          Music is written on five lines. The notes on those five lines, from the bottom up,
          are <strong>E – G – B – D – F</strong>. Some people remember them with mnemonics
          like "Every Good Boy Does Fine" or "Eat Good Bread Dear Father." Jason mentions
          these — but his real advice is different.
        </p>
        <p>
          Notice what separates each line note: E to G is a third (E-F-G). G to B is a third
          (G-A-B). B to D is a third (B-C-D). D to F is a third (D-E-F). Every consecutive
          line is a <strong>third apart</strong>. So if you remember your thirds, you will
          remember your notation. You don't have to mug up the sentence — you just need to
          feel the interval.
        </p>
        <p>
          This is important. You need to be able to count alphabets in both directions —
          ascending (A B C D E F G, and back to A) and descending (G F E D C B A). Practice
          this wherever you are: in a cab, walking in a park, waiting for a flight. Write the
          alphabets in a circle and practice counting clockwise and counterclockwise. You don't
          need a piano for this. It's just alphabets.
        </p>

        <StaffExample caption="The five line notes of the treble clef — E, G, B, D, F — each a third apart.">
          <Notation
            measures={[
              { chords: [{ keys: ["E4"], duration: "w" }] },
              { chords: [{ keys: ["G4"], duration: "w" }] },
              { chords: [{ keys: ["B4"], duration: "w" }] },
              { chords: [{ keys: ["D5"], duration: "w" }] },
              { chords: [{ keys: ["F5"], duration: "w" }] },
            ]}
            timeSig="4/4"
            clef="treble"
            width={620}
            height={140}
          />
        </StaffExample>
      </Folio>

      <Folio number="III" title="The spaces — FACE">
        <p>
          The four spaces between the lines (bottom to top) are <strong>F – A – C – E</strong>.
          Jason acknowledges this is easy to remember because it spells <em>face</em> — "quite
          a common word, I guess." Good. Use it.
        </p>
        <p>
          The same third-interval principle applies here: space to space is also a third. F to
          A is a third. A to C. C to E. So whether you're moving line-to-line or space-to-space,
          you are always skipping by a third. The whole staff is built on thirds.
        </p>

        <StaffExample caption="The four space notes — F, A, C, E — spelling FACE.">
          <Notation
            measures={[
              { chords: [{ keys: ["F4"], duration: "w" }] },
              { chords: [{ keys: ["A4"], duration: "w" }] },
              { chords: [{ keys: ["C5"], duration: "w" }] },
              { chords: [{ keys: ["E5"], duration: "w" }] },
            ]}
            timeSig="4/4"
            clef="treble"
            width={520}
            height={140}
          />
        </StaffExample>
      </Folio>

      <Folio number="IV" title="Ledger lines — and the most important note in notation">
        <p>
          Music has far more than nine notes (5 lines + 4 spaces). To go higher or lower than
          the staff allows, we add <strong>ledger lines</strong> — short extra lines drawn for
          a single note, extending the range. Jason calls it a "strange word" but it's official:
          ledger lines. You draw just a small one, not the full width, otherwise people get
          confused with the real five lines.
        </p>
        <p>
          Below the treble clef, count down from E in thirds: E → C → A. C sits on one ledger
          line below the staff. This is <strong>middle C</strong> — the C roughly in the
          centre of your piano, the note you should sit with at the centre of the keyboard
          when you position yourself. It's one of the most important reference points in all
          of music notation. Below middle C, one more third down, is A.
        </p>
        <p>
          Above the staff, count up from F in thirds: F → A → C. A sits in the space just
          above the top line, C sits on the first ledger line above. The same principle
          continues as far as you want to go.
        </p>
        <Marginalia>
          Modern notation apps like MuseScore label notes as C4, C5, C6 — the number is which
          octave on the piano. Middle C is C4 because it's the fourth C counting from the
          low end of a grand piano. This is "just random information. It's not so important,"
          Jason says, but good to know.
        </Marginalia>
      </Folio>

      <Engraving
        src="/notes/staff-notation/finals/02-notes-on-the-treble-clef-page1.png"
        caption="Jason's full treble clef map — lines E G B D F, spaces F A C E, ledger lines above and below, with middle C marked."
      />

      <Folio number="V" title="Accidentals and the key signature">
        <p>
          The white notes of the piano are E G B D F on the lines and F A C E in the spaces.
          What about sharps and flats — the black notes? Very easy: you place the symbol{" "}
          <em>before</em> the note head. In everyday writing we say "E flat" — in notation
          it goes <strong>flat then E</strong>, reading left to right. The flat (or sharp)
          always comes first.
        </p>
        <p>
          When a whole piece is in a key with multiple sharps or flats — E major has four
          sharps: F♯, C♯, G♯, D♯ — you don't write them before every note. You use a{" "}
          <strong>key signature</strong>: all the sharps (or flats) written in order at the
          start of every line, before the music begins. This tells the player: any time you
          see F, play it as F♯ automatically. The key signature is a signature — unique,
          like a personal signature — telling you which scale you're on.
        </p>
        <Glossarium>
          <p>
            <strong>Sharp (♯)</strong> — raise the note one semitone. Written before the note.
          </p>
          <p>
            <strong>Flat (♭)</strong> — lower the note one semitone. Written before the note.
          </p>
          <p>
            <strong>Natural (♮)</strong> — cancels a sharp or flat. If your key signature has
            F♯ but you want a plain F, you write a natural sign before that particular note.
            "Normal C would be the natural sign," Jason says. You can also use a natural to
            add an accidental outside the key — if you're in C major and want a G♯, you write
            a sharp before the G. These are the five extra notes the key signature didn't give
            you, available any time via accidentals.
          </p>
        </Glossarium>
        <Marginalia>
          "You have to dedicate about an hour or two a week for the process of notation."
          — Jason on the real work of learning to read music.
        </Marginalia>
      </Folio>
    </LessonShell>
  );
}
